import React from 'react';
import '../assets/css/layout/legal.css';

const Privacy = () => {
    return (
        <div className="legal-container">
            <div className="legal-header">
                <h1>Privacy Policy</h1>
                <p className="last-updated">Last Updated: January 21, 2026</p>
            </div>

            <div className="legal-content">
                <section className="legal-section">
                    <h2>1. Introduction</h2>
                    <p>
                        E-AquaHealth (&quot;we,&quot; &quot;our,&quot; or &quot;us&quot;) is committed to protecting your privacy. 
                        This Privacy Policy explains how we collect, use, disclose, and safeguard your 
                        information when you use our platform, including our website and mobile applications.
                    </p>
                    <p>
                        By using E-AquaHealth, you consent to the data practices described in this policy. 
                        If you do not agree with our policies, please do not use our services.
                    </p>
                </section>

                <section className="legal-section">
                    <h2>2. Information We Collect</h2>
                    
                    <h3>2.1 Personal Information</h3>
                    <p>We may collect personal information that you voluntarily provide, including:</p>
                    <ul>
                        <li><strong>Account Information:</strong> Name, email address, phone number, password</li>
                        <li><strong>Profile Information:</strong> Profile photo, business name, location, professional credentials</li>
                        <li><strong>Payment Information:</strong> Billing address, payment card details (processed securely by our payment providers)</li>
                        <li><strong>Communication Data:</strong> Messages, consultation records, support inquiries</li>
                    </ul>

                    <h3>2.2 Farm and Business Information</h3>
                    <p>For fish farmers and aquaculture professionals, we collect:</p>
                    <ul>
                        <li>Farm location and size</li>
                        <li>Fish species and stock information</li>
                        <li>Water quality data and test results</li>
                        <li>Health records and case histories</li>
                        <li>Purchase and order history</li>
                    </ul>

                    <h3>2.3 Automatically Collected Information</h3>
                    <p>When you access our platform, we automatically collect:</p>
                    <ul>
                        <li>Device information (type, operating system, browser)</li>
                        <li>IP address and location data</li>
                        <li>Usage patterns and interaction data</li>
                        <li>Cookies and similar tracking technologies</li>
                    </ul>
                </section>

                <section className="legal-section">
                    <h2>3. How We Use Your Information</h2>
                    <p>We use the collected information for:</p>
                    <ul>
                        <li><strong>Service Delivery:</strong> Providing veterinary consultations, laboratory services, and marketplace functionality</li>
                        <li><strong>Account Management:</strong> Creating and managing your account, authentication, and security</li>
                        <li><strong>Communication:</strong> Sending notifications, updates, and responding to inquiries</li>
                        <li><strong>Improvement:</strong> Analyzing usage to improve our services and user experience</li>
                        <li><strong>Personalization:</strong> Customizing content and recommendations based on your preferences</li>
                        <li><strong>Legal Compliance:</strong> Meeting regulatory requirements and protecting our legal rights</li>
                        <li><strong>Marketing:</strong> Sending promotional materials (with your consent)</li>
                    </ul>
                </section>

                <section className="legal-section">
                    <h2>4. Information Sharing</h2>
                    <p>We may share your information with:</p>
                    
                    <h3>4.1 Service Providers</h3>
                    <p>
                        Veterinarians, laboratories, and vendors on our platform receive relevant information 
                        to provide their services. For example, a veterinarian will see your case details 
                        and farm information to provide consultations.
                    </p>

                    <h3>4.2 Third-Party Service Providers</h3>
                    <p>We work with trusted partners for:</p>
                    <ul>
                        <li>Payment processing</li>
                        <li>Cloud hosting and data storage</li>
                        <li>Analytics and performance monitoring</li>
                        <li>Email and notification services</li>
                    </ul>

                    <h3>4.3 Legal Requirements</h3>
                    <p>
                        We may disclose information when required by law, court order, or government request, 
                        or to protect our rights, property, or safety.
                    </p>

                    <h3>4.4 Business Transfers</h3>
                    <p>
                        In the event of a merger, acquisition, or sale of assets, your information may be 
                        transferred as part of the transaction.
                    </p>
                </section>

                <section className="legal-section">
                    <h2>5. Data Security</h2>
                    <p>We implement robust security measures to protect your information:</p>
                    <ul>
                        <li><strong>Encryption:</strong> All data transmission uses SSL/TLS encryption</li>
                        <li><strong>Secure Storage:</strong> Data is stored on secure, access-controlled servers</li>
                        <li><strong>Access Controls:</strong> Employee access to personal data is limited and monitored</li>
                        <li><strong>Regular Audits:</strong> We conduct security assessments and vulnerability testing</li>
                        <li><strong>Incident Response:</strong> We have procedures to handle potential data breaches</li>
                    </ul>
                    <p>
                        While we strive to protect your information, no method of transmission over the 
                        internet is 100% secure. We cannot guarantee absolute security.
                    </p>
                </section>

                <section className="legal-section">
                    <h2>6. Your Rights and Choices</h2>
                    <p>You have the following rights regarding your personal information:</p>
                    <ul>
                        <li><strong>Access:</strong> Request a copy of your personal data</li>
                        <li><strong>Correction:</strong> Update or correct inaccurate information</li>
                        <li><strong>Deletion:</strong> Request deletion of your account and associated data</li>
                        <li><strong>Portability:</strong> Receive your data in a structured, machine-readable format</li>
                        <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications</li>
                        <li><strong>Restriction:</strong> Request limitation of processing in certain circumstances</li>
                    </ul>
                    <p>
                        To exercise these rights, contact us at privacy@eaquahealth.com or through 
                        your account settings.
                    </p>
                </section>

                <section className="legal-section">
                    <h2>7. Cookies and Tracking</h2>
                    <p>We use cookies and similar technologies for:</p>
                    <ul>
                        <li><strong>Essential Cookies:</strong> Required for basic platform functionality</li>
                        <li><strong>Analytics Cookies:</strong> Help us understand how users interact with our platform</li>
                        <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
                        <li><strong>Marketing Cookies:</strong> Used to deliver relevant advertisements</li>
                    </ul>
                    <p>
                        You can control cookies through your browser settings. Disabling certain cookies 
                        may affect platform functionality.
                    </p>
                </section>

                <section className="legal-section">
                    <h2>8. Data Retention</h2>
                    <p>
                        We retain your personal information for as long as your account is active or as 
                        needed to provide services. We may retain certain information for longer periods 
                        as required by law or for legitimate business purposes, such as:
                    </p>
                    <ul>
                        <li>Transaction records (7 years for financial compliance)</li>
                        <li>Veterinary consultation records (as required by veterinary regulations)</li>
                        <li>Legal claims and disputes resolution</li>
                    </ul>
                </section>

                <section className="legal-section">
                    <h2>9. Children&apos;s Privacy</h2>
                    <p>
                        E-AquaHealth is not intended for users under 18 years of age. We do not knowingly 
                        collect personal information from children. If you believe we have collected 
                        information from a minor, please contact us immediately.
                    </p>
                </section>

                <section className="legal-section">
                    <h2>10. International Data Transfers</h2>
                    <p>
                        Your information may be transferred to and processed in countries other than your 
                        country of residence. We ensure appropriate safeguards are in place to protect 
                        your information in compliance with applicable data protection laws.
                    </p>
                </section>

                <section className="legal-section">
                    <h2>11. Changes to This Policy</h2>
                    <p>
                        We may update this Privacy Policy from time to time. We will notify you of 
                        significant changes by email or through a notice on our platform. Your continued 
                        use of E-AquaHealth after changes indicates acceptance of the updated policy.
                    </p>
                </section>

                <section className="legal-section">
                    <h2>12. Contact Us</h2>
                    <p>
                        If you have questions or concerns about this Privacy Policy or our data practices, 
                        please contact us:
                    </p>
                    <div className="contact-info">
                        <p><strong>Email:</strong> privacy@eaquahealth.com</p>
                        <p><strong>Data Protection Officer:</strong> dpo@eaquahealth.com</p>
                        <p><strong>Address:</strong> E-AquaHealth Headquarters</p>
                    </div>
                </section>
            </div>
        </div>
    );
};

export default Privacy;
